// Styles de base des modales (overlay, en-tête, pied) partagés avec l'ancienne modale
import '../../../pages/TruckAssignments/manual-assignment-modal.css';
import '../../../pages/TruckAssignments/reschedule-date-modal.css';

import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

const pluralize = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;

/**
 * Ce que la modale annonce d'un camion, sous son nom : ce qui le bloque, son
 * exclusion, un départ par défaut, ou le regroupement de ses commandes
 * (mêmes règles que tms-chargeur)
 *
 * @param {object} vehicle - Le camion de l'aperçu
 * @param {boolean} isChecked - Il part avec l'envoi
 * @returns {{text: string, tone: string}|null} - La ligne, ou rien
 */
export function describeDispatchVehicle(vehicle, isChecked) {
  if (vehicle.blocking) {
    return { text: vehicle.blocking.message, tone: 'alert' };
  }
  if (!isChecked) {
    return {
      text: 'Exclu : ses affectations restent modifiables',
      tone: 'muted',
    };
  }
  if (vehicle.is_default_departure) {
    return {
      text: 'heure par défaut (pas de créneau de départ sur le camion)',
      tone: 'warn',
    };
  }
  if (vehicle.grouped_orders_count > 0) {
    return {
      text: `${vehicle.grouped_orders_count} commandes regroupées (même adresse)`,
      tone: 'muted',
    };
  }
  return null;
}

/**
 * Le libellé du bouton : nombre de camions et de commandes envoyés
 *
 * @param {Array<object>} vehicles - Les camions cochés et envoyables
 * @returns {string} - Le libellé
 */
export function describeDispatchButton(vehicles) {
  if (vehicles.length === 0) return 'Aucun camion à envoyer';
  const orders = vehicles.reduce(
    (sum, vehicle) => sum + vehicle.orders_count,
    0
  );
  return `Envoyer ${pluralize(vehicles.length, 'camion')} · ${pluralize(
    orders,
    'commande'
  )}`;
}

/**
 * La notification d'un envoi réussi : ce qui est parti (même texte que
 * tms-chargeur). Les échecs sont notifiés à part, camion par camion.
 *
 * @param {object} result - La réponse de l'envoi
 * @returns {string} - Le texte
 */
export function describeDispatchResult(result) {
  const tours = result.tours || [];
  const orders = tours.reduce((sum, tour) => sum + tour.orders_count, 0);

  return `${pluralize(tours.length, 'tournée')} envoyée${
    tours.length > 1 ? 's' : ''
  } en exploitation · ${tours
    .map((tour) => tour.vehicule_key)
    .join(', ')} · ${pluralize(orders, 'commande')}`;
}

/**
 * Modale « Envoyer en exploitation » : un camion par ligne (ceux qui ont une
 * tournée en préparation à la date), coché par défaut, décochable pour
 * l'exclure. Un camion que l'API sait voué à l'échec (étape sans
 * coordonnées…) est affiché avec son motif, décoché et non cochable.
 *
 * @param {object} props - Les propriétés
 * @param {boolean} props.isOpen - Ouverture
 * @param {Function} props.onClose - Fermeture
 * @param {string} props.date - La date affichée (YYYY-MM-DD)
 * @param {Array<object>} props.trucks - Les camions de l'écran (couleurs)
 * @param {string|null} [props.onlyVehicleKey] - N'envoyer que ce camion (menu du camion)
 * @param {Function} props.loadPreview - (date) => Promise<object|null>
 * @param {Function} props.onConfirm - (vehiculeKeys) => Promise<boolean> : vrai ferme la modale
 * @returns {JSX.Element|null} - La modale
 */
export default function DispatchModal({
  isOpen,
  onClose,
  date,
  trucks,
  onlyVehicleKey = null,
  loadPreview,
  onConfirm,
}) {
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set());
  const [isSaving, setIsSaving] = useState(false);

  // L'aperçu est lu à l'ouverture : les rappels vivent dans des refs pour que
  // leur recréation à chaque rendu de la page ne relance pas la lecture
  const loadPreviewRef = useRef(loadPreview);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    loadPreviewRef.current = loadPreview;
    onCloseRef.current = onClose;
  }, [loadPreview, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let isCurrent = true;
    setPreview(null);

    const load = async () => {
      const data = await loadPreviewRef.current(date);
      if (!isCurrent) return;
      if (!data) {
        onCloseRef.current();
        return;
      }
      setPreview(data);
      setExcluded(
        new Set(
          data.vehicles
            .filter(
              (vehicle) =>
                onlyVehicleKey && vehicle.vehicule_key !== onlyVehicleKey
            )
            .map((vehicle) => vehicle.vehicule_key)
        )
      );
    };
    load();

    return () => {
      isCurrent = false;
    };
  }, [isOpen, date, onlyVehicleKey]);

  if (!isOpen) return null;

  const colorByKey = new Map(trucks.map((truck) => [truck.key, truck.color]));
  const rows = preview?.vehicles || [];
  const toSend = rows.filter(
    (vehicle) => !vehicle.blocking && !excluded.has(vehicle.vehicule_key)
  );
  const assignedCount = rows.reduce(
    (sum, vehicle) => sum + vehicle.orders_count,
    0
  );

  const toggle = (vehiculeKey) =>
    setExcluded((current) => {
      const next = new Set(current);
      if (next.has(vehiculeKey)) next.delete(vehiculeKey);
      else next.add(vehiculeKey);
      return next;
    });

  const confirm = async () => {
    setIsSaving(true);
    try {
      if (await onConfirm(toSend.map((vehicle) => vehicle.vehicule_key))) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const close = () => {
    if (!isSaving) onClose();
  };

  return (
    <div className="modal-overlay" onClick={close}>
      <div
        className="modal-container ta-dispatch-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Envoyer en exploitation</h2>
            <p className="modal-subtitle">
              {[
                moment(date).format('ddd D MMM YYYY'),
                preview
                  ? `${pluralize(assignedCount, 'commande')} affectée${
                      assignedCount > 1 ? 's' : ''
                    } sur ${pluralize(rows.length, 'camion')}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}{' '}
              — crée les tournées (web et application chauffeur), puis
              verrouille les camions dans cet écran.
            </p>
          </div>
          <button
            className="modal-close-btn"
            onClick={close}
            disabled={isSaving}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {!preview ? (
            <p className="ta-dispatch-modal__loading">
              Chargement des camions de la date…
            </p>
          ) : rows.length === 0 ? (
            <p className="ta-dispatch-modal__loading">
              Aucun camion à envoyer : aucune tournée en préparation à cette
              date.
            </p>
          ) : (
            <>
              <span className="ta-dispatch-modal__label">
                Camions à envoyer · décocher pour exclure
              </span>
              <div className="ta-dispatch-modal__list">
                {rows.map((vehicle) => {
                  const isBlocked = Boolean(vehicle.blocking);
                  const isChecked =
                    !isBlocked && !excluded.has(vehicle.vehicule_key);
                  const note = describeDispatchVehicle(vehicle, isChecked);

                  return (
                    <label
                      key={vehicle.vehicule_key}
                      className={`ta-dispatch-modal__row ${
                        isChecked ? '' : 'ta-dispatch-modal__row--dimmed'
                      } ${isBlocked ? 'ta-dispatch-modal__row--blocked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isBlocked || isSaving}
                        onChange={() => toggle(vehicle.vehicule_key)}
                      />
                      <span
                        className="ta-dispatch-modal__color"
                        style={{
                          backgroundColor:
                            vehicle.color ||
                            colorByKey.get(vehicle.vehicule_key) ||
                            '#9aa0a6',
                        }}
                      />
                      <span className="ta-dispatch-modal__main">
                        <span className="ta-dispatch-modal__name">
                          {[vehicle.vehicule_key, vehicle.imat]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        {note && (
                          <span
                            className={`ta-dispatch-modal__note ta-dispatch-modal__note--${note.tone}`}
                            title={note.text}
                          >
                            {note.text}
                          </span>
                        )}
                      </span>
                      <span className="ta-dispatch-modal__counts">
                        {pluralize(vehicle.orders_count, 'commande')}
                        {isBlocked
                          ? ''
                          : ` · ${pluralize(vehicle.steps_count, 'étape')}`}
                      </span>
                      <span className="ta-dispatch-modal__departure">
                        Départ {vehicle.departure_time}
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="ta-dispatch-modal__info">
                {preview.unassigned_orders_count > 0 &&
                  `${
                    preview.unassigned_orders_count > 1
                      ? `Les ${preview.unassigned_orders_count} commandes non affectées restent`
                      : 'La commande non affectée reste'
                  } « À placer » pour cette date. `}
                Les camions envoyés restent affichés, verrouillés : pour en
                modifier un, supprimez sa tournée (non démarrée) depuis la liste
                des tournées. Un camion en échec est listé avec son motif : ses
                affectations restent modifiables.
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <span className="ta-dispatch-modal__tour-key">
            {toSend[0]
              ? `Clé de tournée : ${toSend[0].tour_key}${
                  toSend.length > 1 ? '…' : ''
                }`
              : ''}
          </span>
          <button className="btn-secondary" onClick={close} disabled={isSaving}>
            Annuler
          </button>
          <button
            className="btn-primary"
            onClick={confirm}
            disabled={!preview || toSend.length === 0 || isSaving}
          >
            {isSaving ? 'Envoi en cours…' : describeDispatchButton(toSend)}
          </button>
        </div>
      </div>
    </div>
  );
}
